import "./bulk-tender-documents.css";

import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { Button } from "@tql/ui-components-react/tql/buttons";
import type { SyntheticEvent } from "react";

import { TenderDocumentOptionsFields } from "~/shared/components/tender-document-options-fields/tender-document-options-fields.tsx";

import type { LabelSize } from "../../../quote/components/shipment-details/components/tender-modal/constants/tender-modal-constants.ts";
import { flaggedLabelSizes } from "../../../quote/components/shipment-details/components/tender-modal/constants/tender-modal-constants.ts";
import type { BulkTenderDocumentFormData } from "../../schemas/bulk-tender-document-schema.ts";

const MAX_ADDITIONAL_DETAILS_LENGTH = 150;

type BulkTenderDocumentsProps = {
	isLoading?: boolean;
	isSendEnabled: boolean;
	onDownload: (formData: BulkTenderDocumentFormData) => void;
	onSend: (formData: BulkTenderDocumentFormData) => void;
	onSubscribe: () => void;
};

const labelSizeValidator = (value: LabelSize | null) => {
	if (!value?.labelSize) return "Size is required";
	return "";
};

const labelsPerShipmentValidator = (value: number | null) => {
	if (!value || value < 1) return "Labels per shipment must be at least 1";
	if (value > 999) return "Labels per shipment cannot exceed 999";
	return "";
};

const additionalDetailsValidator = (value?: string) => {
	if (value && value.length > MAX_ADDITIONAL_DETAILS_LENGTH) {
		return `Additional details cannot exceed ${MAX_ADDITIONAL_DETAILS_LENGTH} characters`;
	}
	return "";
};

const getFormData = (
	dataItem: Record<string, unknown>,
): BulkTenderDocumentFormData => ({
	labelSize: dataItem["labelSize"] as LabelSize,
	labelsPerShipment: dataItem["labelsPerShipment"] as number,
	additionalDetails: dataItem["additionalDetails"] as string,
});

export const BulkTenderDocuments = ({
	isLoading = false,
	isSendEnabled,
	onDownload,
	onSend,
	onSubscribe,
}: BulkTenderDocumentsProps) => {
	const handleSubmit = (
		dataItem: Record<string, unknown>,
		event?: SyntheticEvent<unknown, Event>,
	) => {
		if (!event) return;

		const submitter = (event.nativeEvent as SubmitEvent)
			.submitter as HTMLButtonElement | null;
		const formData = getFormData(dataItem);

		switch (submitter?.name) {
			case "download": {
				onDownload(formData);
				break;
			}
			case "send": {
				if (isSendEnabled) onSend(formData);
				break;
			}
			default: {
				break;
			}
		}
	};

	return (
		<Form
			ignoreModified={true}
			initialValues={{
				additionalDetails: "",
				labelSize: flaggedLabelSizes[0],
				labelsPerShipment: 1,
			}}
			onSubmit={handleSubmit}
			render={(formRenderProps: FormRenderProps) => {
				return (
					<FormElement>
						<div className={"bulk-tender-documents"}>
							<p className={"bulk-tender-documents-message"}>
								Prepare your shipping labels for each shipment before sending
								the BOL and labels to the shipper.
							</p>
							<div className={"bulk-tender-documents-input-group"}>
								<TenderDocumentOptionsFields
									additionalDetailsId={"bulkTenderAdditionalDetails"}
									additionalDetailsLabel={
										"Additional Details Shown on All BOLs"
									}
									additionalDetailsMaxLength={MAX_ADDITIONAL_DETAILS_LENGTH}
									additionalDetailsName={"additionalDetails"}
									additionalDetailsValidator={additionalDetailsValidator}
									characterCountAriaLive={"polite"}
									classNames={{
										additional: "bulk-tender-documents-additional",
										additionalHeader: "bulk-tender-documents-additional-header",
										characterCount: "bulk-tender-documents-character-count",
										input: "bulk-tender-documents-input",
										label: "bulk-tender-documents-label",
										labelCountInput: "bulk-tender-documents-label-count",
										row: "bulk-tender-documents-row",
										sizeInput: "bulk-tender-documents-size",
									}}
									formRenderProps={formRenderProps}
									isLoading={isLoading}
									labelCountId={"bulkTenderLabelsPerShipment"}
									labelCountLabel={
										"Labels per Shipment: Count for Each Handling Unit"
									}
									labelCountName={"labelsPerShipment"}
									labelCountValidator={labelsPerShipmentValidator}
									labelOptions={flaggedLabelSizes}
									labelSizeId={"bulkTenderLabelSize"}
									labelSizeName={"labelSize"}
									labelSizeValidator={labelSizeValidator}
								/>
							</div>

							<div className={"bulk-tender-documents-actions"}>
								<Button
									disabled={isLoading}
									onClick={onSubscribe}
									variant={"secondary"}
								>
									Subscribe to Notifications
								</Button>
								<div className={"bulk-tender-documents-actions-right"}>
									<Button
										disabled={isLoading}
										formAction={"submit"}
										name={"download"}
										variant={"secondary"}
									>
										Download
									</Button>
									{isSendEnabled ? (
										<Button
											disabled={isLoading}
											formAction={"submit"}
											name={"send"}
											variant={"primary"}
										>
											Send
										</Button>
									) : null}
								</div>
							</div>
						</div>
					</FormElement>
				);
			}}
		/>
	);
};
